// Live's hover previews of its transitions (#240 part 2c, spec E1 decision 20) and its effects
// (#265 part 3b, spec effects.md decision 13): the tile or card under the pointer plays its
// transition or its effect, drawn by our own compositor, not a file from a CDN. The tabs' tiles
// wait 120 ms first, as live's do; Transition Settings' cards start at once.
import { useEffect, useRef, useState } from "react";

import type { EffectKind, TransitionKind } from "../../api/generated/model";
import { engineSupported } from "./engine/support";
import type { Previewer } from "./engine/hover-preview";

/** Live's wait before a tile's preview plays. */
export const TILE_PREVIEW_DELAY_MS = 120;

let previewer: Promise<Previewer | null> | null = null;

/** The one previewer, made on the first hover; none where the browser cannot draw it. */
function previewerOnce(): Promise<Previewer | null> {
  previewer ??= engineSupported()
    ? import("./engine/hover-preview").then((module) => module.createPreviewer()).catch(() => null)
    : Promise.resolve(null);
  return previewer;
}

/** What a preview plays: a transition or an effect, by its kind. */
type Shown = { transition: TransitionKind } | { effect: EffectKind };

/**
 * A tile's or a card's preview: `slot` is where the preview's canvas goes (over the card), and
 * `hover` the handlers for the element the pointer rests on. Nothing plays for nothing shown.
 */
function usePreview(shown: Shown | null, delayMs: number) {
  const slot = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  // The kind, not the object, decides when the preview starts again.
  const transition = shown !== null && "transition" in shown ? shown.transition : null;
  const effect = shown !== null && "effect" in shown ? shown.effect : null;
  useEffect(() => {
    const host = slot.current;
    if (!hovered || host === null || (transition === null && effect === null)) return;
    let made: Previewer | null = null;
    let gone = false;
    const timer = setTimeout(() => {
      void previewerOnce().then((ready) => {
        if (gone || ready === null) return;
        made = ready;
        if (transition !== null) ready.show(host, transition);
        else if (effect !== null) ready.showEffect(host, effect);
      });
    }, delayMs);
    return () => {
      gone = true;
      clearTimeout(timer);
      made?.hide(host);
    };
  }, [hovered, transition, effect, delayMs]);
  return {
    slot,
    hover: { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) },
  };
}

/** A transition tile's or card's preview; nothing plays for a kind not drawn. */
export function useTransitionPreview(kind: TransitionKind | null, delayMs: number) {
  return usePreview(kind === null ? null : { transition: kind }, delayMs);
}

/** An effect tile's preview: its effect over the sample picture, its clip again and again. */
export function useEffectPreview(kind: EffectKind, delayMs: number) {
  return usePreview({ effect: kind }, delayMs);
}
